import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
} from "@/components/ui/dialog";
import { ChevronDown, Search, Check, Briefcase } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFundSelectorController } from "./useFundSelectorController";
import { ManageFunds } from "../ManageFunds/ManageFunds";

interface FundSelectorProps {
  className?: string;
}

export const FundSelector: React.FC<FundSelectorProps> = ({ className }) => {
  const controller = useFundSelectorController();

  return (
    <div className={className}>
      <Button
        variant="ghost"
        role="combobox"
        aria-expanded={controller.open}
        onClick={() => controller.setOpen(true)}
        className={cn(
          "flex items-center gap-2 px-2 py-1.5 h-auto",
          "text-left justify-between hover:bg-muted/40",
          "rounded-md w-full",
          "p-0 m-0 translate-x-[-5px]"
        )}
      >
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center h-6 w-6 bg-primary/10 text-primary rounded-full shrink-0">
            <Briefcase className="h-3 w-3" />
          </div>
          <div className="flex flex-col items-start overflow-hidden">
            <span className="text-xs text-muted-foreground font-normal leading-none">
              Fund
            </span>
            <span className="text-sm truncate max-w-[120px] font-medium">
              {controller.selectedFund?.name || "Select Fund"}
            </span>
          </div>
        </div>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      </Button>

      <Dialog open={controller.open} onOpenChange={controller.setOpen}>
        <DialogOverlay className="bg-background/80 backdrop-blur-sm" />
        <DialogContent className="sm:max-w-[425px] max-h-[80vh] overflow-hidden p-0">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Select Fund</DialogTitle>
          </DialogHeader>

          <div className="p-4 pb-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search funds..."
                value={controller.searchQuery}
                onChange={(e) => controller.setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="p-2 max-h-[300px] overflow-y-auto">
            {controller.filteredFunds.length === 0 ? (
              <p className="text-center py-6 text-muted-foreground">
                No funds found
              </p>
            ) : (
              <div className="space-y-1">
                {controller.filteredFunds.map((fund) => (
                  <Button
                    key={fund.fundId}
                    variant="ghost"
                    className="w-full justify-start h-auto py-2.5 px-2 hover:bg-accent/50"
                    onClick={() => controller.selectFund(fund)}
                  >
                    <div className="flex items-center w-full gap-2">
                      <span className="text-middle">{fund.name}</span>
                      {fund.fundId === controller.selectedFund?.fundId && (
                        <Check className="h-4 w-4 text-primary ml-auto align-left" />
                      )}
                    </div>
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="p-4 border-t">
            <ManageFunds onChange={() => controller.refresh()} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
